import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// VideoPlayerModal — Feb 2026.
//
// Renders a Bunny Stream iframe embed inside a full-screen dark overlay. Users
// hit Esc or click the backdrop to dismiss. We use Bunny's iframe player so we
// don't have to manage HLS.js on our own — the player handles adaptive bitrate,
// captions, and mobile fullscreen out of the box.
//
// Oct 2026 — download affordance restored. The project modal was previously
// view-only, so collaborators (contributor role) who could upload videos
// couldn't pull them back down. Mirrors the fetch→blob pattern in
// AssetDetailModal so Safari / Chrome / Firefox all route the stream through
// the download manager instead of opening a new tab.

function _sanitize(name) {
  return (name || "video")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "video";
}

export default function VideoPlayerModal({ video, projectName, canDownload: canDownloadProp = true, onClose }) {
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while the modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Download is gated on role (contributor+). MediaPanel/VideosPanel pass
  // `canDownload` based on effectiveRole; viewer-role users see the player
  // without the save-to-disk button.
  const canDownload = canDownloadProp && !!video?.mp4_url;

  const handleDownload = async () => {
    if (saving || !video?.mp4_url) return;
    const base = _sanitize(projectName || video.title);
    const filename = `${base}_${_sanitize(video.title)}.mp4`;
    setSaving(true);
    let objectUrl = null;
    try {
      const res = await fetch(video.mp4_url, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(`Download failed: ${err?.message || err}`);
    } finally {
      if (objectUrl) {
        // Delay revoke slightly so Safari has time to hand the blob
        // off to the download manager.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      }
      setSaving(false);
    }
  };

  if (!video?.iframe_url) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="video-player-modal"
    >
      <div
        className="relative w-full max-w-6xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="video-player-close"
          aria-label="Close"
          className="absolute -top-10 right-0 text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide"
        >
          <X className="w-4 h-4" />
          Close
        </button>
        <div className="aspect-video bg-black rounded-md overflow-hidden shadow-2xl border border-zinc-800">
          {video.mp4_url ? (
            <video
              key={video.mp4_url}
              controls
              autoPlay
              playsInline
              poster={video.thumbnail_url || undefined}
              className="w-full h-full bg-black"
              data-testid="video-player-html5"
            >
              <source src={video.mp4_url} type="video/mp4" />
              {video.hls_url && <source src={video.hls_url} type="application/vnd.apple.mpegurl" />}
              Your browser doesn&apos;t support HTML5 video.
            </video>
          ) : (
            <iframe
              src={`${video.iframe_url}${video.iframe_url.includes("?") ? "&" : "?"}autoplay=true`}
              title={video.title}
              loading="lazy"
              className="w-full h-full border-0"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 text-zinc-300">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-light truncate" title={video.title}>
              {video.title}
            </h2>
            {video.uploader_name && (
              <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-wide">
                Uploaded by {video.uploader_name}
              </p>
            )}
          </div>
          {canDownload && (
            <Button
              type="button"
              data-testid="video-player-download-btn"
              onClick={handleDownload}
              disabled={saving}
              title="Download the original MP4"
              className="bg-amber-500 hover:bg-amber-400 text-black h-10 px-5 gap-2 shrink-0"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Download MP4
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
